import { useState, useEffect } from 'react'
import {  useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { LoginHeader } from '../../onboarding/LoginHeader'
import { FcGoogle } from 'react-icons/fc'
import { UTMLink } from '@/components/custom-ui/utm-link'
import { Loader2 } from 'lucide-react'
import { env, isPreview } from '@/lib/config'
import { useAnalytics } from '@/lib/analytics'
import { getUtmParams } from '@/lib/utils'

// Use UTMLink for preserving UTM parameters in links
const Link = UTMLink

interface AuthProps {
  headerSlot?: React.ReactNode;
  redirectTo?: string;
  view?: 'sign_in' | 'sign_up' | 'invite';
  hideLabels?: boolean;
}

export function Auth({ headerSlot, redirectTo, view = 'sign_in' }: AuthProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [authMode, setAuthMode] = useState<'sign_in' | 'sign_up'>(view === 'sign_up' ? 'sign_up' : 'sign_in')
  const [usePassword, setUsePassword] = useState(false)
  const [searchParams] = useSearchParams()
  const flow = searchParams.get('flow')
  const action = searchParams.get('action')
  const sessionToken = searchParams.get('sessionToken')
  const isSignupFlow = flow === 'signup' || authMode === 'sign_up'
  const isInviteFlow = flow === 'invite' || view === 'invite'
  const isClaimFlow = action === 'claim' && sessionToken
  const shouldCreateUser = isSignupFlow || isInviteFlow || isClaimFlow
  const selectedPlan = searchParams.get('plan')
  const token = searchParams.get('token')
  // Check for invitation token in search params or extract from redirectTo
  let invitationToken = searchParams.get('invitation_token') || token
  
  // If no invitation token in params but redirectTo contains one, extract it
  if (!invitationToken && redirectTo && redirectTo.includes('invitation_token=')) {
    const match = redirectTo.match(/invitation_token=([^&]+)/)
    if (match) {
      invitationToken = match[1]
    }
  }
  const { trackEvent, events } = useAnalytics()

  // Store session token for brand claiming if it's a claim flow
  useEffect(() => {
    if (isClaimFlow && sessionToken) {
      localStorage.setItem('brand_claim_session_token', sessionToken);
      console.log('[Auth] Stored brand claim session token:', sessionToken);
    }
  }, [isClaimFlow, sessionToken])
  
  console.log('[Auth] Initialized with invitation token:', invitationToken, 'redirect:', redirectTo)

  // Track page view with flow information
  useEffect(() => {
    trackEvent(shouldCreateUser ? events.USER_SIGNED_UP : events.USER_LOGGED_IN, {
      method: 'page_load',
      selected_plan: selectedPlan,
      flow_type: flow || 'login',
      referrer: document.referrer
    })
  }, [shouldCreateUser, events, trackEvent, selectedPlan, flow])

  useEffect(() => {
    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        try {
          // Check if marketing profile exists
          const { data: profile } = await supabase
            .schema('marketing_and_customer_success')
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single()

          const metadata = session.user.user_metadata || {}
          const email = session.user.email
          
          // Check if user is coming from an invitation
          const isFromInvitation = invitationToken || window.location.pathname.includes('process-invitation')

          // If no profile exists, create one with comprehensive data
          if (!profile) {
            const { error: profileError } = await supabase
              .schema('marketing_and_customer_success')
              .from('profiles')
              .upsert({
                id: session.user.id,
                full_name: metadata.full_name || metadata.name || email?.split('@')[0],
                email: email,
                avatar_url: metadata.avatar_url || metadata.picture,
                customer_status: 'lead',
                lifecycle_stage: 'awareness',
                account_tier: 'standard',
                first_contact_date: new Date().toISOString(),
                last_login_date: new Date().toISOString(),
                marketing_opt_in: true,
                preferred_language: metadata.language || metadata.locale || 'en',
                timezone: metadata.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                login_count: 1,
                onboarding_completed: isFromInvitation ? true : false,
                email_opt_in: metadata.email_verified ? true : false,
                metadata: {
                  provider: 'email',
                  selected_plan: selectedPlan,
                  ...metadata
                }
              })
            
            if (profileError) throw profileError

            // Redirect based on invitation status
            if (isFromInvitation) {
              // Don't redirect here - let the process-invitation page handle it
              return
            } else {
              // Redirect to onboarding for new users
              window.location.href = '/onboarding'
            }
          } else {
            // If profile exists, update login-related fields
            const { error: updateError } = await supabase
              .schema('marketing_and_customer_success')
              .from('profiles')
              .update({
                last_login_date: new Date().toISOString(),
                login_count: (profile.login_count || 0) + 1,
                full_name: metadata.full_name || metadata.name,
                email: email,
                avatar_url: metadata.avatar_url || metadata.picture,
                metadata: {
                  provider: 'email',
                  ...metadata
                }
              })
              .eq('id', session.user.id)

            if (updateError) throw updateError

            // Check if user has completed onboarding
            const { data: updatedProfile } = await supabase
              .schema('marketing_and_customer_success')
              .from('profiles')
              .select('onboarding_completed')
              .eq('id', session.user.id)
              .single()

            // Check if user is coming from an invitation
            const isFromInvitation = invitationToken || window.location.pathname.includes('process-invitation')
            
            if (isFromInvitation) {
              // Don't redirect here - let the process-invitation page handle it
              return
            }

            // Redirect based on onboarding status
            window.location.href = updatedProfile?.onboarding_completed ? '/dashboard' : '/onboarding'
          }
        } catch (error) {
          console.error('Error handling auth state change:', error)
          window.location.href = '/'
        }
      } else if (event === 'SIGNED_OUT') {
        // Redirect to home page on sign out
        window.location.replace('/')
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [selectedPlan])

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setLoading(true)
      setError(null)

      // Track auth attempt
      trackEvent(shouldCreateUser ? events.USER_SIGNED_UP : events.USER_LOGGED_IN, {
        method: usePassword ? 'email_password' : 'email_magic_link',
        email_domain: email.split('@')[1],
        selected_plan: selectedPlan,
        flow_type: flow || 'login',
        has_invitation: !!invitationToken
      })
      
      // Determine the appropriate redirect based on whether this is an invitation
      let redirectUrl = `${window.location.origin}/auth/callback`;
      
      // Get UTM parameters to pass along
      const utmParams = getUtmParams();
      const utmQueryString = Object.keys(utmParams).length > 0 
        ? '&' + new URLSearchParams(utmParams).toString() 
        : '';
      
      // If this is an invitation, always redirect to process-invitation
      if (invitationToken) {
        redirectUrl = `${window.location.origin}/process-invitation?invitation_token=${invitationToken}${utmQueryString}`;
        console.log('[Auth] Using invitation-specific redirect with UTM:', redirectUrl);
      } else if (isClaimFlow && sessionToken) {
        // Brand claiming flow - redirect to callback with claim context
        redirectUrl = `${window.location.origin}/auth/callback?claim=true&sessionToken=${sessionToken}${utmQueryString}`;
        console.log('[Auth] Using brand claim redirect with UTM:', redirectUrl);
      } else if (redirectTo) {
        // Use custom redirect if provided and no invitation
        redirectUrl = `${window.location.origin}${redirectTo.startsWith('/') ? '' : '/'}${redirectTo}`;
        // Add UTM parameters to custom redirect
        if (Object.keys(utmParams).length > 0) {
          redirectUrl += redirectUrl.includes('?') ? '&' + new URLSearchParams(utmParams).toString() : '?' + new URLSearchParams(utmParams).toString();
        }
        console.log('[Auth] Using custom redirect with UTM:', redirectUrl);
      } else {
        // Add UTM parameters to the default callback URL
        if (Object.keys(utmParams).length > 0) {
          redirectUrl += '?' + new URLSearchParams(utmParams).toString();
        }
      }

      if (usePassword) {
        // Sign in/up with password
        if (authMode === 'sign_up' || shouldCreateUser) {
          const { error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              emailRedirectTo: redirectUrl,
            }
          })
          if (error) throw error
          
          // For password signup, we need to handle the redirect differently
          // since the user might need to confirm their email
          setMagicLinkSent(true) // Reuse this state to show confirmation message
        } else {
          const { error } = await supabase.auth.signInWithPassword({
            email,
            password
          })
          if (error) throw error
          
          // Password sign in is immediate, no need to wait
          // The auth state change listener will handle the redirect
        }
      } else {
        // Sign in with OTP/magic link
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: redirectUrl,
            shouldCreateUser: Boolean(shouldCreateUser),
          }
        })

        if (error) throw error
        setMagicLinkSent(true)
      }
      
      // Track auth success
      if (!usePassword || (usePassword && (authMode === 'sign_up' || shouldCreateUser))) {
        trackEvent(events.FEATURE_USED, {
          feature: usePassword ? 'password_signup' : 'magic_link_sent',
          success: true,
          is_signup: shouldCreateUser,
          flow_type: flow || 'login',
          email_domain: email.split('@')[1]
        })
      }
    } catch (error) {
      console.error('Error during auth:', error)
      let errorMessage = error instanceof Error ? error.message : 'Authentication failed'
      
      // Check if this is the "signups not allowed" error - means user doesn't exist
      if (errorMessage.toLowerCase().includes('signups not allowed') || errorMessage.toLowerCase().includes('signup not allowed')) {
        errorMessage = `No account found for ${email}. Would you like to create an account instead?`
      }
      
      setError(errorMessage)
      
      // Track auth error
      trackEvent(events.ERROR_OCCURRED, {
        error_type: 'auth_failed',
        method: usePassword ? 'email_password' : 'email_magic_link',
        is_signup: shouldCreateUser,
        flow_type: flow || 'login',
        error_message: error instanceof Error ? error.message : String(error)
      })
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      setLoading(true)
      setError(null)

      // Track Google auth attempt
      trackEvent(shouldCreateUser ? events.USER_SIGNED_UP : events.USER_LOGGED_IN, {
        method: 'google_oauth',
        is_signup: shouldCreateUser,
        flow_type: flow || 'login',
        selected_plan: selectedPlan,
        has_invitation: !!invitationToken
      })

      // Determine the appropriate redirect
      let redirectUrl = `${window.location.origin}/auth/callback`;
      
      // Get UTM parameters to pass along
      const utmParams = getUtmParams();
      const utmQueryString = Object.keys(utmParams).length > 0 
        ? '&' + new URLSearchParams(utmParams).toString() 
        : '';
      
      // If this is an invitation, always redirect to process-invitation
      if (invitationToken) {
        redirectUrl = `${window.location.origin}/process-invitation?invitation_token=${invitationToken}${utmQueryString}`;
        console.log('[Auth] Google login with invitation-specific redirect and UTM:', redirectUrl);
      } else if (isClaimFlow && sessionToken) {
        // Brand claiming flow - redirect to callback with claim context
        redirectUrl = `${window.location.origin}/auth/callback?claim=true&sessionToken=${sessionToken}${utmQueryString}`;
        console.log('[Auth] Google login with brand claim redirect and UTM:', redirectUrl);
      } else if (redirectTo) {
        // Use custom redirect if provided and no invitation
        redirectUrl = `${window.location.origin}${redirectTo.startsWith('/') ? '' : '/'}${redirectTo}`;
        // Add UTM parameters to custom redirect
        if (Object.keys(utmParams).length > 0) {
          redirectUrl += redirectUrl.includes('?') ? '&' + new URLSearchParams(utmParams).toString() : '?' + new URLSearchParams(utmParams).toString();
        }
        console.log('[Auth] Google login with custom redirect and UTM:', redirectUrl);
      } else {
        // Add UTM parameters to the default callback URL
        if (Object.keys(utmParams).length > 0) {
          redirectUrl += '?' + new URLSearchParams(utmParams).toString();
        }
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
            redirect_uri: `${env.VITE_SUPABASE_URL}/auth/v1/callback`
          }
        },
      })

      if (error) throw error
      
      // We won't track success here because the user will be redirected to Google
    } catch (error) {
      console.error('Error signing in with Google:', error)
      setError('Error signing in with Google')
      
      // Track auth error
      trackEvent(events.ERROR_OCCURRED, {
        error_type: 'auth_failed',
        method: 'google_oauth',
        is_signup: shouldCreateUser,
        flow_type: flow || 'login',
        error_message: error instanceof Error ? error.message : String(error)
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col">
      {!isInviteFlow && (headerSlot || <LoginHeader />)}

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-6">
          {view === 'invite' && (
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold mb-2">Complete Your Account Setup</h2>
              <p className="text-muted-foreground">
                Choose how you'd like to access your account
              </p>
            </div>
          )}

          {isPreview && (
            <div className="mb-4 p-2 bg-gray-100 rounded text-xs break-all">
              <p>You are looking at a preview environment.</p>
              <p>Supabase URL: {env.VITE_SUPABASE_URL || 'Not set'}</p>
              <p>Redirect URL: {`${env.VITE_SUPABASE_URL}/auth/v1/callback`}</p>
            </div>
          )}

          <div className="space-y-6">
            <div>
              {error && (
                <div className="text-center mt-2">
                  <p className="text-red-500 text-sm">{error}</p>
                  {error.includes('Would you like to create an account instead?') && !isSignupFlow && (
                    <div className="mt-2">
                      <Link 
                        to="/login?flow=signup" 
                        className="text-primary hover:underline text-sm font-medium"
                      >
                        Create an account
                      </Link>
                    </div>
                  )}
                </div>
              )}
              {magicLinkSent && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-700 font-medium">
                    {usePassword && (authMode === 'sign_up' || shouldCreateUser) 
                      ? 'Account created! Check your email to confirm your account.'
                      : 'Magic link sent! Check your email to sign in.'}
                  </p>
                </div>
              )}
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <FcGoogle className="mr-2 h-5 w-5" />
              Continue with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with email
                </span>
              </div>
            </div>

            <form onSubmit={handleAuth} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {usePassword && (
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                )}
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setUsePassword(!usePassword)}
                  className="text-sm text-muted-foreground hover:text-primary"
                >
                  {usePassword ? 'Use magic link instead' : 'Use password instead'}
                </button>
              </div>

              <div className="space-y-2">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading
                    </>
                  ) : (
                    usePassword 
                      ? (authMode === 'sign_up' || shouldCreateUser ? 'Create Account' : 'Sign In')
                      : (authMode === 'sign_up' || shouldCreateUser ? 'Create Account' : 'Send Magic Link')
                  )}
                </Button>

                {view === 'invite' ? (
                  <div className="text-center text-sm">
                    <button
                      type="button"
                      onClick={() => setAuthMode(authMode === 'sign_in' ? 'sign_up' : 'sign_in')}
                      className="text-primary hover:underline"
                    >
                      {authMode === 'sign_in' 
                        ? "Don't have an account? Create one" 
                        : "Already have an account? Sign in"}
                    </button>
                  </div>
                ) : (
                  !isInviteFlow && (
                    <>
                      {!isSignupFlow && (
                        <div className="text-center text-sm">
                          <span className="text-muted-foreground">
                            Don't have an account?{' '}
                          </span>
                          <Link to="/login?flow=signup" className="text-primary hover:underline">
                            Create one
                          </Link>
                        </div>
                      )}

                      {isSignupFlow && (
                        <div className="text-center text-sm">
                          <span className="text-muted-foreground">
                            Already have an account?{' '}
                          </span>
                          <Link to="/login" className="text-primary hover:underline">
                            Sign in
                          </Link>
                        </div>
                      )}
                    </>
                  )
                )}
              </div>
            </form>
          </div>
        </Card>
      </div>
    </div>
  )
}